import { Injectable } from '@angular/core';

export interface FirebaseErrorInfo {
  code: string;
  message: string;
  browser: string;
  userAgent: string;
  timestamp: Date;
}

@Injectable({
  providedIn: 'root'
})
export class FirebaseErrorHandlerService {

  /**
   * Trata erros específicos do Firebase Messaging
   */
  handleMessagingError(error: any): FirebaseErrorInfo {
    const errorInfo: FirebaseErrorInfo = {
      code: this.extractErrorCode(error),
      message: this.extractErrorMessage(error),
      browser: this.getBrowserInfo(),
      userAgent: navigator.userAgent,
      timestamp: new Date()
    };

    // Log do erro para debugging
    console.error('Firebase Messaging Error:', errorInfo);

    // Tratamento específico por tipo de erro
    switch (errorInfo.code) {
      case 'messaging/unsupported-browser':
        this.handleUnsupportedBrowserError(errorInfo);
        break;
      case 'messaging/indexed-db-unsupported':
        this.handleIndexedDbError(errorInfo);
        break;
      case 'messaging/permission-blocked':
        this.handlePermissionBlockedError(errorInfo);
        break;
      case 'messaging/permission-default':
        this.handlePermissionDefaultError(errorInfo);
        break;
      case 'messaging/unknown-error':
        this.handleUnknownError(errorInfo);
        break;
      default:
        this.handleGenericError(errorInfo);
    }

    return errorInfo;
  }

  /**
   * Extrai o código do erro
   */
  private extractErrorCode(error: any): string {
    if (error?.code) {
      return error.code;
    }

    if (error?.message) {
      // Tenta extrair o código da mensagem
      const codeMatch = error.message.match(/\(([^)]+)\)/);
      if (codeMatch) {
        return codeMatch[1];
      }
    }

    return 'messaging/unknown-error';
  }

  /**
   * Extrai a mensagem do erro
   */
  private extractErrorMessage(error: any): string {
    if (error?.message) {
      return error.message;
    }

    return 'Erro desconhecido do Firebase Messaging';
  }

  /**
   * Obtém informações do navegador
   */
  private getBrowserInfo(): string {
    const userAgent = navigator.userAgent;

    if (userAgent.includes('Chrome')) {
      return 'Chrome';
    } else if (userAgent.includes('Firefox')) {
      return 'Firefox';
    } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
      return 'Safari';
    } else if (userAgent.includes('Edge')) {
      return 'Edge';
    } else if (userAgent.includes('Opera')) {
      return 'Opera';
    }

    return 'Unknown';
  }

  /**
   * Trata erro de navegador não suportado
   */
  private handleUnsupportedBrowserError(errorInfo: FirebaseErrorInfo): void {
    console.warn('Navegador não suporta Firebase Messaging:', {
      browser: errorInfo.browser,
      userAgent: errorInfo.userAgent
    });

    // Pode implementar fallback ou notificação ao usuário
    this.showUserNotification(
      'Navegador não suportado',
      'Seu navegador não suporta notificações push. Recomendamos usar Chrome, Firefox ou Edge.'
    );
  }

  /**
   * Trata erro de IndexedDB não suportado
   */
  private handleIndexedDbError(errorInfo: FirebaseErrorInfo): void {
    console.warn('IndexedDB não é suportado:', errorInfo);

    this.showUserNotification(
      'Funcionalidade limitada',
      'Seu navegador não suporta todas as funcionalidades necessárias para notificações push.'
    );
  }

  /**
   * Trata erro de permissão bloqueada
   */
  private handlePermissionBlockedError(errorInfo: FirebaseErrorInfo): void {
    console.warn('Permissão de notificação bloqueada:', errorInfo);

    this.showUserNotification(
      'Permissão bloqueada',
      'As notificações push estão bloqueadas. Para receber notificações, permita-as nas configurações do navegador.'
    );
  }

  /**
   * Trata erro de permissão padrão
   */
  private handlePermissionDefaultError(errorInfo: FirebaseErrorInfo): void {
    console.warn('Permissão de notificação não definida:', errorInfo);

    this.showUserNotification(
      'Permissão necessária',
      'Para receber notificações push, você precisa permitir o acesso às notificações quando solicitado.'
    );
  }

  /**
   * Trata erro desconhecido (incluindo timeout)
   */
  private handleUnknownError(errorInfo: FirebaseErrorInfo): void {
    console.warn('Erro desconhecido do Firebase Messaging:', errorInfo);

    // Verifica se é um erro de timeout
    if (errorInfo.message === 'Timeout') {
      this.showUserNotification(
        'Problema de conexão',
        'Houve um problema de conexão ao configurar as notificações. Tente recarregar a página ou verificar sua conexão com a internet.'
      );
    } else {
      this.showUserNotification(
        'Erro técnico',
        'Ocorreu um erro técnico ao configurar as notificações. Tente novamente mais tarde.'
      );
    }
  }

  /**
   * Trata erro genérico
   */
  private handleGenericError(errorInfo: FirebaseErrorInfo): void {
    console.error('Erro genérico do Firebase Messaging:', errorInfo);

    this.showUserNotification(
      'Erro de notificação',
      'Ocorreu um erro ao configurar as notificações. Tente recarregar a página.'
    );
  }

  /**
   * Mostra notificação ao usuário (pode ser implementado com toast ou modal)
   */
  private showUserNotification(title: string, message: string): void {
    // Implementação básica - pode ser melhorada com um serviço de toast
    console.log(`[${title}] ${message}`);

    if (!('Notification' in window) || Notification.permission !== 'granted') {
      return;
    }

    const options: NotificationOptions = { body: message };

    // Chrome Android (PWA / SW) rejeita `new Notification()` com TypeError e exige
    // ServiceWorkerRegistration.showNotification() — ver NX-UI-WEBAPP-ANGULAR-1FW.
    try {
      new Notification(title, options);
    } catch {
      if ('serviceWorker' in navigator) {
        void navigator.serviceWorker.ready
          .then((registration) => registration.showNotification(title, options))
          .catch(() => undefined);
      }
    }
  }

  /**
   * Verifica se o erro é relacionado ao Firebase Messaging
   */
  isFirebaseMessagingError(error: any): boolean {
    const code = this.extractErrorCode(error);
    return code.startsWith('messaging/');
  }

  /**
   * Verifica se o erro é de navegador não suportado
   */
  isUnsupportedBrowserError(error: any): boolean {
    return this.extractErrorCode(error) === 'messaging/unsupported-browser';
  }

  /**
   * Obtém sugestões de solução baseadas no erro
   */
  getErrorSuggestions(error: any): string[] {
    const code = this.extractErrorCode(error);
    const suggestions: string[] = [];

    switch (code) {
      case 'messaging/unsupported-browser':
        suggestions.push('Use um navegador moderno como Chrome, Firefox ou Edge');
        suggestions.push('Atualize seu navegador para a versão mais recente');
        suggestions.push('Verifique se o JavaScript está habilitado');
        break;
      case 'messaging/indexed-db-unsupported':
        suggestions.push('Use um navegador que suporte IndexedDB');
        suggestions.push('Verifique se não está em modo privado/incógnito');
        break;
      case 'messaging/permission-blocked':
        suggestions.push('Acesse as configurações do navegador');
        suggestions.push('Procure por "Notificações" ou "Permissões"');
        suggestions.push('Permita notificações para este site');
        break;
      case 'messaging/permission-default':
        suggestions.push('Clique em "Permitir" quando solicitado');
        suggestions.push('Recarregue a página e tente novamente');
        break;
      default:
        suggestions.push('Recarregue a página');
        suggestions.push('Limpe o cache do navegador');
        suggestions.push('Tente em um navegador diferente');
    }

    return suggestions;
  }
}
